/**
 * What an account held before its first statement line.
 *
 * Without this the ledger starts every account at nil, so the first import
 * makes it disagree with the bank by exactly the amount that was already
 * there. Nothing warns: the trial balance still sums to zero because the error
 * is symmetrical, and the bank account is simply wrong.
 *
 * The number to enter is on the statement, usually labelled opening balance,
 * and the date is the day before the first line. Both are read off the same
 * page, which is why they are asked for together rather than derived — a
 * derived opening balance is one nobody checked against the paper.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { Card, ErrorNote, Field, Note } from '../components/ui'

interface AccountRow {
  id: string
  name: string
  opening_balance_paise: number | null
  opening_balance_date: string | null
}

/** Rupees as typed, into paise. Integer paise everywhere, converted at the edge. */
function toPaise(text: string): number | null {
  const cleaned = text.replace(/[₹,\s]/g, '')
  if (!cleaned || !/^-?\d*\.?\d{0,2}$/.test(cleaned)) return null
  return Math.round(Number(cleaned) * 100)
}

export default function OpeningBalance({ accounts }: { accounts: AccountRow[] }) {
  const queryClient = useQueryClient()
  const [accountId, setAccountId] = useState('')
  const [amount, setAmount] = useState('')
  const [asAt, setAsAt] = useState('')
  const [done, setDone] = useState<string | null>(null)

  const waiting = accounts.filter((a) => !a.opening_balance_date)

  const record = useMutation({
    mutationFn: () =>
      api.post<{ journal_number: string }>(
        `/banking/accounts/${accountId}/opening-balance`,
        { balance_paise: toPaise(amount), as_at: asAt },
      ),
    onSuccess: (entry) => {
      setDone(entry.journal_number)
      setAmount('')
      setAsAt('')
      setAccountId('')
      void queryClient.invalidateQueries({ queryKey: ['bank-accounts'] })
      void queryClient.invalidateQueries({ queryKey: ['bank-txns'] })
    },
  })

  if (waiting.length === 0) return null

  const paise = toPaise(amount)
  const ready = accountId && asAt && paise !== null && paise !== 0

  return (
    <Card
      title="Record an opening balance"
      hint={`${waiting.length} account${waiting.length === 1 ? '' : 's'} still starting from nil`}
    >
      <ErrorNote error={record.error} />
      {done && (
        <Note kind="ok">
          Recorded as {done}. The other side went to Opening balance equity, which is where
          anything not yet accounted for sits until it is.
        </Note>
      )}

      <Note kind="warn">
        Until this is recorded the ledger starts the account at nil, so posting a statement
        makes the books disagree with the bank by whatever was already in it. Nothing warns
        about that — the trial balance still sums to zero, because the error is on both
        sides.
      </Note>

      <div className="row">
        <Field label="Account">
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Choose an account…</option>
            {waiting.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Balance" caption="in rupees; negative if it was overdrawn">
          <input
            className="mono"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="13465.28"
          />
        </Field>
        <Field label="As at" caption="the day before the first statement line">
          <input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} />
        </Field>
      </div>

      {amount.trim() !== '' && paise === null && (
        <Note kind="warn">That is not an amount. Rupees and paise, like 13465.28.</Note>
      )}
      {paise !== null && paise !== 0 && (
        <Note kind="info">
          {formatInr(Math.abs(paise))} {paise > 0 ? 'held' : 'overdrawn'} on {asAt || 'the day you choose'}.
        </Note>
      )}

      <button
        className="primary"
        disabled={!ready || record.isPending}
        onClick={() => record.mutate()}
      >
        {record.isPending ? 'Recording…' : 'Record it'}
      </button>
    </Card>
  )
}
